/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IArticle } from '../../actions/DataServiceEntities.g';
import ArticleCollectionCard from './components/articleCollectionCard';
import classNames from 'classnames';
import { ILobasArticleCollectionData } from './lobas-article-collection.data';
import { ILobasArticleCollectionProps, ILobasArticleCollectionResources } from './lobas-article-collection.props.autogenerated';
import { UncontrolledPagination } from '@msdyn365-commerce-modules/utilities';
import { getCurrentUrl, hydrateRefinersFromUrl } from '../components/lobas-url-utils';
import { reaction } from 'mobx';
import { ListPageState } from '../../actions/lobas-articles-list-page-state';
import getCollectionArticles, {
    GetFullArticlesByCollectionInput,
    IFullArticleResultsWithCount
} from '../../actions/get-all-articles.action';
import { SortColumn } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

export interface ILobasArticleCollectionViewProps extends ILobasArticleCollectionProps<ILobasArticleCollectionData> {
    article: IArticle;
    resources: ILobasArticleCollectionResources;
    locale: string;
}

export interface ILobasArticleCollectionState {
    articles: IArticle[];
    currentPageNumber: number;
    totalArticleCount: number;
}
/**
 *
 * LobasArticleCollection component
 * @extends {React.PureComponent<ILobasArticleCollectionProps<ILobasArticleCollectionData>>}
 */
class LobasArticleCollection extends React.PureComponent<
    ILobasArticleCollectionProps<ILobasArticleCollectionData>,
    ILobasArticleCollectionState
> {
    public constructor(props: ILobasArticleCollectionProps<ILobasArticleCollectionData>, state: ILobasArticleCollectionState) {
        super(props);
        this.state = { articles: [], currentPageNumber: 0, totalArticleCount: 0 };
    }

    //private readonly defaultPageNumber: number = 10;
    private isListPageStateUpdated: boolean = false;

    public async componentDidMount() {
        console.log(this.props.data.articles);
        /*void this.props.data.articles.then(res => {
            this.setState({
                articles: res.articles,
                totalArticleCount: res.count,
                currentPageNumber:
                    (this.props.context.request.query &&
                        +this.props.context.request.query.skip / (this.props.config.itemsPerPage ?? this.defaultPageNumber)) ||
                    0
            });
        });*/

        const listPageState = await this.props.data.listPageState;
        const articles = await this.props.data.articles;

        this._updateListPageState(listPageState, articles);

        // Initialize reaction based on listPageState properties
        reaction(
            () => {
                return [
                    listPageState.activeFilters?.length,
                    listPageState.currentPageNumber,
                    ArrayExtensions.hasElements(listPageState.sortingCritera?.Columns)
                ];
            },
            async () => {
                const newListPageState = await this.props.data.listPageState;
                const input = new GetFullArticlesByCollectionInput(
                    this.props.context.request.apiSettings,
                    {
                        Paging: {
                            Top: this.props.config.itemsPerPage,
                            Skip: Math.max(newListPageState.pageSize * (newListPageState.currentPageNumber ?? 0), 0)
                        },
                        count: true,
                        Sorting: newListPageState.sortingCritera ?? {}
                    },
                    newListPageState.activeFilters ?? [],
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- RecordId is string. Check required.

                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Required check.
                    this.props.context.request.locale
                );

                const articleResult = await getCollectionArticles(input, this.props.context.actionContext);
                newListPageState.totalTableCount = articleResult.count;
                newListPageState.activeArticles = articleResult.articles;
            }
        );
    }

    public render(): JSX.Element | null {
        const {
            data: { listPageState, articles: articles }
        } = this.props;
        const rootClassName = classNames('lobasArticleCollection', this.props.config.className);

        if (!this.isListPageStateUpdated && listPageState.result && articles.result) {
            this._updateListPageState(listPageState.result, articles.result);
            this.isListPageStateUpdated = true;
        }

        if (this.props.data.articles.status === 'FAILED') {
            return <div className={rootClassName}>{this.props.resources.noArticlesLabel}</div>;
        } else {
            //var articles = this.state['articles'];

            var filteredArticles = articles.result
                ? articles.result.articles.filter((a: IArticle) => {
                      if (a.FilterKeywords && this.props.config.filterByTags) {
                          const keywords = a.FilterKeywords.split(',').map(k => k.trim());
                          return this.props.config.filterByTags.every(k => keywords.includes(k));
                      } else {
                          if (this.props.config.filterByTags) {
                              return false;
                          } else {
                              return true;
                          }
                      }
                  })
                : [];
            return (
                <div className={rootClassName}>
                    {filteredArticles.map((a: IArticle) => {
                        const articleCollectionViewProps = {
                            article: a,
                            resources: this.props.resources,
                            locale: this.props.context.request.locale
                        } as ILobasArticleCollectionViewProps;
                        return (
                            // eslint-disable-next-line react/jsx-key
                            <a href={`${this.props.context.request.url.requestUrl.origin}${this.props.config.articlePageURL}/${a.URL}`}>
                                <ArticleCollectionCard {...articleCollectionViewProps}></ArticleCollectionCard>
                            </a>
                        );
                    })}
                    {this._getPagination()}
                </div>
            );
        }
    }

    private readonly _getPagination = (): React.ReactNode => {
        const { config, context, resources, data } = this.props;

        const listPageState = data.listPageState.result;
        const fullUrl = getCurrentUrl(context.request);
        const itemsPerPage = config.itemsPerPage || 10;
        // eslint-disable-next-line max-len -- existing code
        const skipCount = listPageState?.currentPageNumber
            ? listPageState.currentPageNumber * (this.props.config.itemsPerPage || 10)
            : (this.props.context.request.query && +this.props.context.request.query.skip) || 0;
        const totalItems = listPageState?.totalTableCount || 0;
        const previousText = resources.flipperPrevious;
        const nextText = resources.flipperNext;

        if (totalItems <= itemsPerPage) {
            return null;
        }
        return (
            <UncontrolledPagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={resources.paginationAriaLabel}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                //telemetryContent={this.telemetryContent}
            />
        );
    };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className='ms-search-result__pagination-left' aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className='ms-search-result__pagination-right' aria-hidden='true' />
            </div>
        );
    };

    /**
     * Update the list page state with initial data.
     * @param listPageState -- List page State.
     * @param products -- Products.
     */
    private readonly _updateListPageState = (listPageState: ListPageState, articles: IFullArticleResultsWithCount): void => {
        let querySorting: SortColumn[] = [];
        const defaultPageNumber: number = 10;

        if (this.props.context.request.query?.sorting) {
            querySorting = JSON.parse(decodeURIComponent(this.props.context.request.query.sorting)) as SortColumn[];
        }

        listPageState.currentPageNumber =
            (this.props.context.request.query &&
                +this.props.context.request.query.skip / (this.props.config.itemsPerPage ?? defaultPageNumber)) ||
            0;
        listPageState.sortingCritera = { Columns: querySorting };
        listPageState.pageSize = this.props.config.itemsPerPage ?? defaultPageNumber;
        listPageState.activeArticles = articles.articles;
        listPageState.totalTableCount = articles.count;
        listPageState.activeFilters = hydrateRefinersFromUrl(this.props.context.request);
    };
}

export default LobasArticleCollection;
